/**
 * Bulk Edit – left config column (info + route/amount + drop options).
 * Direct form fields, no card wrappers — matching creator style.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Settings2, Sliders } from 'lucide-react';

import { DropOptionsSection, StaticTagsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import { RouteAmountSection as BulkRouteAmountSection } from '@/features/smswiser-campaigns/components/prepared-drop-modals/sections/RouteAmountSection';
import type { Gateway, ShortLinkTld } from '@/hooks';
import type { useBulkEditPreparedDropsForm } from '@/features/smswiser-campaigns/components/prepared-drop-modals/useBulkEditPreparedDropsForm';
import { ColumnHeader } from '../shared';

type BulkForm = ReturnType<typeof useBulkEditPreparedDropsForm>;

interface Props {
  bulkCount: number;
  gateways: Gateway[];
  tagsByGatewayId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: ShortLinkTld[];
  bulkForm: BulkForm;
  isBusy: boolean;
  countryKey?: string;
}

export function ScaleDropModalBulkEditTopLeft(props: Props) {
  const { bulkCount, gateways, campaigns, tlds, isBusy, bulkForm, tagsByGatewayId, tagColors, countryKey } = props;

  return (
    <div className="flex flex-1 flex-col">
      <ColumnHeader
        icon={Settings2}
        title="Configuration"
        subtitle="Route & Schedule"
        badge={
          <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            {bulkCount === 1 ? 'Edit' : `${bulkCount} drafts`}
          </span>
        }
      />

      {/* ONE scroll container for the whole column. Drop Options and Static
          Tags used to sit below it as shrink-0 blocks: on shorter viewports
          they were clipped by the modal with no way to scroll to them. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-4 p-3">
        <div className="rounded-md bg-muted/30 p-2.5 text-sm">
          <p className="font-medium text-foreground">
            {bulkCount === 1 ? 'Editing 1 draft' : `Editing ${bulkCount} drafts`}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {bulkCount === 1 ? 'All fields will be saved.' : 'Only filled fields will be applied.'}
          </p>
        </div>

        <BulkRouteAmountSection
          mode="bulk"
          stack
          gateways={gateways.map((g) => ({ id: g.id, name: g.name, pricePerSms: g.pricePerSms }))}
          campaigns={campaigns}
          countryKey={countryKey}
          wiserId={bulkForm.wiserId}
          setWiserId={bulkForm.setWiserId}
          tlds={tlds.map((t) => ({ value: t.value, displayName: t.displayName }))}
          routeId={bulkForm.routeId}
          setRouteId={bulkForm.setRouteId}
          tldValue={bulkForm.tldValue}
          setTldValue={bulkForm.setTldValue}
          amount={bulkForm.amount}
          setAmount={bulkForm.setAmount}
          executeAfter={bulkForm.executeAfter}
          setExecuteAfter={bulkForm.setExecuteAfter}
          clearSchedule={bulkForm.clearSchedule}
          onRemoveSchedule={bulkForm.onRemoveSchedule}
          scheduleInterval={bulkForm.scheduleInterval}
          setScheduleInterval={bulkForm.setScheduleInterval}
          costInfo={bulkForm.costInfo}
          count={bulkCount}
          isBusy={isBusy}
          tagsByGatewayId={tagsByGatewayId}
          tagColors={tagColors}
        />
        </div>

      <div className="border-t border-border/40">
        <div className="flex items-center gap-1.5 bg-muted/20 px-4 py-2">
          <Sliders className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Drop Options</span>
        </div>
        <div className="p-3">
          <DropOptionsSection
            hideTitle
            compact
            enableBatchesAndAi
            allowUnchanged={bulkCount > 1}
            amount={bulkForm.amount}
            forceSend={bulkForm.forceSend}
            setForceSend={bulkForm.setForceSend}
            sendInBatches={bulkForm.sendInBatches}
            setSendInBatches={bulkForm.setSendInBatches}
            batchAmount={bulkForm.batchAmount === '' ? undefined : bulkForm.batchAmount}
            setBatchAmount={(v) => bulkForm.setBatchAmount(v ?? '')}
            batchDelay={bulkForm.batchDelay === '' ? undefined : bulkForm.batchDelay}
            setBatchDelay={(v) => bulkForm.setBatchDelay(v ?? '')}
            aiOptimizationEnabled={bulkForm.aiOptimizationEnabled}
            setAiOptimizationEnabled={bulkForm.setAiOptimizationEnabled}
            aiOptimizationType={bulkForm.aiOptimizationType}
            setAiOptimizationType={bulkForm.setAiOptimizationType}
            aiOptimizationBatchAmount={bulkForm.aiOptimizationBatchAmount}
            setAiOptimizationBatchAmount={bulkForm.setAiOptimizationBatchAmount}
            aiOptimizationMinimalValuePercent={bulkForm.aiOptimizationMinimalValuePercent}
            setAiOptimizationMinimalValuePercent={bulkForm.setAiOptimizationMinimalValuePercent}
            aiOptimizationSendUntil={bulkForm.aiOptimizationSendUntil}
            setAiOptimizationSendUntil={bulkForm.setAiOptimizationSendUntil}
            useOnlyNewDomains={bulkForm.useOnlyNewDomains}
            setUseOnlyNewDomains={bulkForm.setUseOnlyNewDomains}
            shortlink={{
              clickIdStructure: bulkForm.clickIdStructure,
              setClickIdStructure: bulkForm.setClickIdStructure,
              domainScheme: bulkForm.domainScheme,
              setDomainScheme: bulkForm.setDomainScheme,
            }}
            isBusy={isBusy}
          />
        </div>
      </div>
      <div className="border-t border-border/40">
        <div className="flex items-center gap-1.5 bg-muted/20 px-4 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Static Tags</span>
        </div>
        <div className="p-3">
          <StaticTagsPickerSection
            mode="single"
            selectedId={bulkForm.staticTagId}
            onChange={(id) => bulkForm.setStaticTagId(id)}
            allowUnchanged={bulkCount > 1}
            disabled={isBusy}
            compact
          />
        </div>
      </div>
      </div>
    </div>
  );
}
